import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { kutukGeo } from '@/src/app/shared/utils/param-deger.utils';
import { StraforKesiciService } from '@/src/app/uygulamalar/strafor-kesici/services/strafor-kesici.service';

@Component({
  selector: 'cam-kutuk-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ke">
      <div class="ke-row">
        <div class="ke-alan">
          <label class="ke-etiket">Kütük ID</label>
          <input nz-input nzSize="small" [ngModel]="p()?.kutukId || ''" (ngModelChange)="guncelle('kutukId', $event)" placeholder="Kütük kodu"/>
        </div>
        <div class="ke-alan">
          <label class="ke-etiket">Proje Adı</label>
          <input nz-input nzSize="small" [ngModel]="p()?.projeAdi || ''" (ngModelChange)="guncelle('projeAdi', $event)" placeholder="Proje adı"/>
        </div>
      </div>

      <div class="ke-gorsel">
        <svg [attr.viewBox]="geo().vb" preserveAspectRatio="xMidYMid meet" class="ke-svg">
          <defs>
            <linearGradient id="kUst" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="#fbbf24"/><stop offset="100%" stop-color="#f59e0b"/></linearGradient>
            <linearGradient id="kOn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#d97706"/><stop offset="100%" stop-color="#b45309"/></linearGradient>
            <linearGradient id="kSag" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#b45309"/><stop offset="100%" stop-color="#92400e"/></linearGradient>
            <marker id="mB" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M1,1 L7,4 L1,7Z" fill="#3b82f6"/></marker>
            <marker id="mG" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M1,1 L7,4 L1,7Z" fill="#22c55e"/></marker>
            <marker id="mR" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M1,1 L7,4 L1,7Z" fill="#ef4444"/></marker>
            <filter id="gs"><feDropShadow dx="1" dy="2" stdDeviation="2" flood-color="#000" flood-opacity=".12"/></filter>
          </defs>

          <!-- Grid arka plan -->
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="var(--py-kenarlik,#e5e7eb)" stroke-width=".3" opacity=".5"/>
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)"/>

          <!-- 3D Kütük -->
          <g filter="url(#gs)">
            <polygon [attr.points]="geo().on" fill="url(#kOn)"/>
            <polygon [attr.points]="geo().sag" fill="url(#kSag)"/>
            <polygon [attr.points]="geo().ust" fill="url(#kUst)"/>
          </g>
          <polygon [attr.points]="geo().on" fill="none" stroke="#92400e" stroke-width="1" stroke-linejoin="round"/>
          <polygon [attr.points]="geo().sag" fill="none" stroke="#78350f" stroke-width="1" stroke-linejoin="round"/>
          <polygon [attr.points]="geo().ust" fill="none" stroke="#a16207" stroke-width="1" stroke-linejoin="round"/>

          <!-- X ölçü (en) — alt -->
          <g [attr.transform]="'translate(0,' + (geo().by + 18) + ')'">
            <line [attr.x1]="geo().bx" y1="0" [attr.x2]="geo().bx + geo().w" y2="0" stroke="#3b82f6" stroke-width="1.5" marker-start="url(#mB)" marker-end="url(#mB)"/>
            <rect [attr.x]="geo().bx + geo().w/2 - 26" y="-7" width="52" height="14" rx="3" fill="var(--py-arkaplan,#fff)" stroke="#3b82f6" stroke-width=".5" opacity=".9"/>
            <text [attr.x]="geo().bx + geo().w/2" y="4" text-anchor="middle" fill="#3b82f6" font-size="9" font-weight="700" font-family="'Consolas',monospace">{{ geo().enD }}</text>
          </g>

          <!-- Z ölçü (yükseklik) — sol -->
          <g [attr.transform]="'translate(' + (geo().bx - 18) + ',0)'">
            <line x1="0" [attr.y1]="geo().by" x2="0" [attr.y2]="geo().by - geo().h" stroke="#ef4444" stroke-width="1.5" marker-start="url(#mR)" marker-end="url(#mR)"/>
            <rect x="-30" [attr.y]="geo().by - geo().h/2 - 7" width="52" height="14" rx="3" fill="var(--py-arkaplan,#fff)" stroke="#ef4444" stroke-width=".5" opacity=".9"/>
            <text x="-4" [attr.y]="geo().by - geo().h/2 + 4" text-anchor="middle" fill="#ef4444" font-size="9" font-weight="700" font-family="'Consolas',monospace">{{ geo().yukD }}</text>
          </g>

          <!-- Y ölçü (boy) — sağ üst -->
          <g>
            <line [attr.x1]="geo().bx + geo().w + 18" [attr.y1]="geo().by" [attr.x2]="geo().bx + geo().w + geo().dh + 18" [attr.y2]="geo().by - geo().dh" stroke="#22c55e" stroke-width="1.5" marker-start="url(#mG)" marker-end="url(#mG)"/>
            <rect [attr.x]="geo().bx + geo().w + geo().dh*.5 + 22" [attr.y]="geo().by - geo().dh*.5 - 7" width="52" height="14" rx="3" fill="var(--py-arkaplan,#fff)" stroke="#22c55e" stroke-width=".5" opacity=".9"/>
            <text [attr.x]="geo().bx + geo().w + geo().dh*.5 + 48" [attr.y]="geo().by - geo().dh*.5 + 4" text-anchor="middle" fill="#22c55e" font-size="9" font-weight="700" font-family="'Consolas',monospace">{{ geo().boyD }}</text>
          </g>

          <!-- Başlık -->
          <text [attr.x]="geo().mx" y="14" text-anchor="middle" fill="var(--py-metin-ucuncul,#94a3b8)" font-size="8" font-weight="600" letter-spacing="2" font-family="system-ui">KÜTÜK ÖLÇÜLERİ</text>
        </svg>
      </div>

      <div class="ke-row">
        <div class="ke-alan">
          <label class="ke-etiket">En (mm)</label>
          <input nz-input nzSize="small" type="number" [ngModel]="p()?.en" (ngModelChange)="guncelle('en', $event)" placeholder="En giriniz"/>
          <span class="ke-aciklama">X ekseni ölçüsü</span>
        </div>
        <div class="ke-alan">
          <label class="ke-etiket">Boy (mm)</label>
          <input nz-input nzSize="small" type="number" [ngModel]="p()?.boy" (ngModelChange)="guncelle('boy', $event)" placeholder="Boy giriniz"/>
          <span class="ke-aciklama">Y ekseni ölçüsü</span>
        </div>
        <div class="ke-alan">
          <label class="ke-etiket">Yükseklik (mm)</label>
          <input nz-input nzSize="small" type="number" [ngModel]="p()?.yukseklik" (ngModelChange)="guncelle('yukseklik', $event)" placeholder="Yükseklik giriniz"/>
          <span class="ke-aciklama">Z ekseni ölçüsü</span>
        </div>
      </div>

      <div class="ke-row">
        <div class="ke-alan" style="flex:0 0 100px">
          <label class="ke-etiket">Adet</label>
          <input nz-input nzSize="small" type="number" [ngModel]="p()?.adet || 1" (ngModelChange)="guncelle('adet', $event)" placeholder="1"/>
        </div>
        <div class="ke-alan" style="flex:1">
          <label class="ke-etiket">Notlar</label>
          <input nz-input nzSize="small" [ngModel]="p()?.notlar || ''" (ngModelChange)="guncelle('notlar', $event)" placeholder="Kısa not yazın..."/>
        </div>
      </div>

      @if (p()?.en && p()?.boy && p()?.yukseklik) {
      <button class="ke-kesici-btn" (click)="straforKesiciyeGit()">
        <span class="ke-kesici-ikon">✂️</span>
        <span>Strafor Kesiciye Gönder</span>
        <span class="ke-kesici-olcu">{{ p().en }}×{{ p().boy }}×{{ p().yukseklik }} mm</span>
      </button>
      }
    </div>
  `,
  styles: [`
    .ke { display:flex; flex-direction:column; gap:8px; }
    .ke-gorsel { display:flex; justify-content:center; padding:0; overflow:hidden; border-radius:8px; border:1px solid var(--py-kenarlik,#f0f0f0); background:var(--py-arkaplan,#fff); }
    .ke-svg { display:block; width:100%; height:160px; }
    .ke-row { display:flex; gap:10px; }
    .ke-alan { display:flex; flex-direction:column; gap:2px; flex:1; padding:4px 0; }
    .ke-etiket { font-weight:500; font-size:12px; color:var(--py-metin-ucuncul, rgba(0,0,0,.45)); letter-spacing:-0.1px; }
    .ke-aciklama { font-size:11px; color:var(--py-metin-devre-disi, rgba(0,0,0,.25)); margin-top:1px; }
    .ke-kesici-btn { display:flex; align-items:center; gap:8px; width:100%; padding:8px 12px; border:1px dashed var(--py-kenarlik,#d9d9d9); border-radius:8px; background:var(--py-arkaplan-soluk,rgba(0,0,0,.02)); cursor:pointer; transition:all .2s;
      &:hover { border-color:var(--py-link,#1890ff); background:rgba(24,144,255,.04); }
    }
    .ke-kesici-ikon { font-size:18px; }
    .ke-kesici-btn > span:nth-child(2) { font-size:13px; font-weight:600; color:var(--py-metin,#333); }
    .ke-kesici-olcu { margin-left:auto; font-size:11px; font-family:monospace; color:var(--py-metin-ucuncul,#999); }
  `],
})
export class CamKutukEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  private strafor = inject(StraforKesiciService);
  constructor() { super(); }

  p = computed(() => this.parametreler()?.ek || {});

  guncelle(alan: string, deger: any) {
    const params = this.parametreler();
    if (!params?.ek) return;
    params.ek[alan] = deger;
    this.parametreler.set({ ...params });
    this._geoTetik.set(Date.now());
  }

  _geoTetik = signal(0);
  geo = computed(() => {
    this._geoTetik();
    const ek = this.parametreler()?.ek || {};
    return kutukGeo(+(ek?.en || 0), +(ek?.boy || 0), +(ek?.yukseklik || 0));
  });

  straforKesiciyeGit() {
    const ek = this.parametreler()?.ek;
    if (!ek?.en || !ek?.boy || !ek?.yukseklik) return;
    this.strafor.hedefParca.set({ en: +ek.en, boy: +ek.boy, yukseklik: +ek.yukseklik });
    this.router.navigate(['/🧩/✂️/kesim-plani']);
  }
}
